import React, { useState } from "react";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/card";
import { FileText, Save, Trash2, Plus, ChevronDown, Code, Settings, Globe, Users, Shield, History, RefreshCw, X } from "lucide-react";
import { ContentManagementTypeEnum, ContentReviewCycleEnum } from "../../modules/shared/model/ContentManagementInterfaces";
import LanguageDropdown from "../../components/ui/LanguageDropdown";
import TranslationModal from "./TranslationModal";
import { useAppDispatch } from "../../redux/store";
import {
  createContentManagementTranslation,
  updateContentManagementTranslation,
  deleteContentManagementTranslation
} from "../../redux/Actions/ContentManagementAction";
import { GlobalService } from "../../modules/shared/services";

interface ContentTabsProps {
  documentType: number;
  contentByType: any;
  setContentByType: (value: any) => void;
  translationsByContent: any;
  setTranslationsByContent: (value: any) => void;
  languages: any[];
  currentUser: any;
  createContent: (payload: any) => Promise<any>;
  updateContent: (id: string, payload: any) => Promise<any>;
  deleteContent: (id: string) => Promise<any>;
  getContentByType: (type: number) => Promise<any>;
  getContentManagementTranslationsByContentId: (id: string) => Promise<any>;
  englishLanguageId: string;
}

const ContentTabs: React.FC<ContentTabsProps> = ({
  documentType,
  contentByType,
  setContentByType,
  translationsByContent,
  setTranslationsByContent,
  languages,
  currentUser,
  createContent,
  updateContent,
  deleteContent,
  getContentByType,
  getContentManagementTranslationsByContentId,
  englishLanguageId
}) => {
  const dispatch = useAppDispatch();
  const [contentTab, setContentTab] = useState("content");
  const [localContent, setLocalContent] = useState({});
  const [selectedCountryContent, setSelectedCountryContent] = useState("all");
  const [contentType, setContentType] = useState("text");
  const [savingContent, setSavingContent] = useState<string | null>(null);
  const [deletingContent, setDeletingContent] = useState<string | null>(null);
  const [expandedSection, setExpandedSection] = useState(null);
  const [selectedSettings, setSelectedSettings] = useState({
    language: { id: "" },
    reviewCycle: { id: 0 }
  });
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [deletingTranslation, setDeletingTranslation] = useState<string | null>(null);

  // Initialize settings from current content when component loads
  React.useEffect(() => {
    const currentContent = contentByType[documentType] || {};
    const contentItems = Object.values(currentContent);
    
    if (contentItems.length > 0) {
      const firstContent = contentItems[0] as any;
      setSelectedSettings({
        language: { id: firstContent.settingLanguage?.id || "" },
        reviewCycle: { id: Number(firstContent.reviewCycle?.id) || 0 }
      });
    }
  }, [contentByType, documentType]);

  // Handle save settings
  const handleSaveSettings = async () => {
    try {
      setIsSavingSettings(true);
      
      if (!selectedSettings?.language?.id || !selectedSettings?.reviewCycle?.id) {
        GlobalService.showToast("error", "Settings Error", "Please select both language and review cycle");
        return;
      }

      const currentContent = contentByType[documentType] || {};
      const contentIds = Object.keys(currentContent);

      if (contentIds.length === 0) {
        GlobalService.showToast("error", "Settings Error", "No content items found to update");
        return;
      }

      // Update each content item with the new settings
      const updatePromises = contentIds.map(async (contentId) => {
        const contentItem = currentContent[contentId];
        
        const updatePayload = {
          ...contentItem,
          type: { id: Number(contentItem?.type?.id) || documentType },
          settingLanguage: { id: selectedSettings.language.id },
          reviewCycle: { id: Number(selectedSettings.reviewCycle.id) }
        };

        await updateContent(contentId, updatePayload);
      });

      // Wait for all updates to complete
      await Promise.all(updatePromises);

      // Refresh content to show updated settings
      const contentResponse = await getContentByType(documentType);
      const freshContent = (contentResponse as any)?.payload?.data || [];

      if (Array.isArray(freshContent)) {
        const transformedContent = {};
        freshContent.forEach((item: any) => {
          transformedContent[item.id] = item;
        });

        setContentByType(prev => ({
          ...prev,
          [documentType]: transformedContent
        }));
      }

      GlobalService.showToast("success", "Settings Updated", "Settings updated successfully for all content items!");
    } catch (error) {
      console.error("Failed to save settings:", error);
      GlobalService.showToast("error", "Settings Error", `Failed to save settings: ${error.message || "Please try again."}`);
    } finally {
      setIsSavingSettings(false);
    }
  };

  const [showTranslationModal, setShowTranslationModal] = useState(false);
  const [selectedTranslation, setSelectedTranslation] = useState(null);
  const [selectedContentForTranslation, setSelectedContentForTranslation] = useState(null);

  // Function to refresh translations after saving
  const refreshTranslations = async () => {
    try {
      const currentContent = contentByType[documentType] || {};
      const translationsData = {};

      for (const [contentId, contentItem] of Object.entries(currentContent)) {
        try {
          const translationsResponse = await getContentManagementTranslationsByContentId(contentId);
          const translations = (translationsResponse as any)?.payload || [];
          translationsData[contentId] = translations;
        } catch (error) {
          console.error(`Failed to fetch translations for content ${contentId}:`, error);
          translationsData[contentId] = [];
        }
      }

      setTranslationsByContent(prev => ({
        ...prev,
        [documentType]: translationsData
      }));
    } catch (error) {
      console.error("Failed to refresh translations:", error);
    }
  };

  // Update localContent when contentByType changes
  React.useEffect(() => {
    const apiContent = contentByType[documentType] || {};
    console.log("ContentTabs: Updating localContent with:", apiContent);
    setLocalContent(apiContent);
  }, [contentByType, documentType]);

  // Reset contentTab to "content" when documentType changes
  React.useEffect(() => {
    setContentTab("content");
  }, [documentType]);

  const safeLocalContent = localContent || {};

  const handleContentChange = (section: string, field: string, value: string) => {
    setLocalContent(prev => ({ ...prev, [section]: { ...prev[section], [field]: value } }));
  };

  const handleSave = async (contentId: string) => {
    try {
      setSavingContent(contentId);
      const contentToUpdate = safeLocalContent[contentId];

      if (!contentToUpdate) {
        console.error("Content not found for ID:", contentId);
        return;
      }

      const originalContent = contentByType[documentType]?.[contentId];

      if (!originalContent) {
        console.error("Original content not found, cannot proceed with update");
        alert("Content not found. Please refresh the page and try again.");
        return;
      }

      const isTextContent = contentType === "text";

      const updatePayload = {
        ...originalContent,
        type: {
          id: Number(originalContent?.type?.id) || documentType
        },
        reviewCycle: {
          id: Number(originalContent?.reviewCycle?.id) || ContentReviewCycleEnum["30Days"]
        },
        contentTitle: contentToUpdate.contentTitle,
        contentDescription: contentToUpdate.contentDescription,
        isText: isTextContent
      };

      await updateContent(contentId, updatePayload);

      setLocalContent(prev => ({
        ...prev,
        [contentId]: {
          ...prev[contentId],
          ...updatePayload
        }
      }));

      const contentResponse = await getContentByType(documentType);
      const freshContent = (contentResponse as any)?.payload?.data || [];

      if (Array.isArray(freshContent)) {
        const transformedContent = {};
        freshContent.forEach((item: any) => {
          transformedContent[item.id] = item;
        });

        setContentByType(prev => ({
          ...prev,
          [documentType]: transformedContent
        }));
      }

      GlobalService.showToast("success", "Content Updated", "Content updated successfully!");
      
      // Close the expanded section after successful save
      setExpandedSection(null);
    } catch (error) {
      console.error(`Failed to update content ${contentId}:`, error);
      GlobalService.showToast("error", "Update Error", "Failed to update content. Please try again.");
    } finally {
      setSavingContent(null);
    }
  };

  const handleDelete = async (contentId: string) => {
    if (!window.confirm("Are you sure you want to delete this content? This action cannot be undone.")) {
      return;
    }

    try {
      setDeletingContent(contentId);
      
      console.log(`[DELETE] Starting deletion of content: ${contentId}`);
      console.log("[DELETE] Current localContent keys:", Object.keys(localContent));
      
      // Delete the content via API
      await deleteContent(contentId);
      console.log(`[DELETE] API deletion successful for: ${contentId}`);

      // Clear expanded section if we're deleting the currently expanded one
      if (expandedSection === contentId) {
        console.log(`[DELETE] Clearing expanded section: ${contentId}`);
        setExpandedSection(null);
      }

      // Update local content immediately for responsive UI
      setLocalContent(prev => {
        const newContent = { ...prev };
        delete newContent[contentId];
        console.log("[DELETE] Updated localContent keys after deletion:", Object.keys(newContent));
        return newContent;
      });

      // Fetch fresh content from API
      console.log(`[DELETE] Fetching fresh content for documentType: ${documentType}`);
      const contentResponse = await getContentByType(documentType);
      const freshContent = (contentResponse as any)?.payload?.data || [];
      console.log("[DELETE] Fresh content received from API:", freshContent);
      console.log("[DELETE] Number of items in fresh content:", freshContent.length);

      // Transform content and update state
      if (Array.isArray(freshContent)) {
        const transformedContent = {};

        // Only process remaining content items
        freshContent.forEach((item: any) => {
          console.log("[DELETE] Processing item:", item.id, item.contentTitle);
          transformedContent[item.id] = item;
        });

        console.log("[DELETE] Transformed content keys:", Object.keys(transformedContent));

        // Update content by type
        setContentByType(prev => ({
          ...prev,
          [documentType]: transformedContent
        }));

        // Update translations - remove translations for deleted content
        setTranslationsByContent(prev => {
          const currentDocTypeTranslations = prev[documentType] || {};
          const newTranslations = { ...currentDocTypeTranslations };
          
          // Remove translations for the deleted content
          delete newTranslations[contentId];
          console.log(`[DELETE] Updated translations, removed translations for: ${contentId}`);
          
          return {
            ...prev,
            [documentType]: newTranslations
          };
        });

        // Update local content to match the fresh data
        setLocalContent(transformedContent);
        console.log("[DELETE] Final localContent update complete");
      } else {
        console.warn("[DELETE] Fresh content is not an array:", freshContent);
      }

      GlobalService.showToast("success", "Content Deleted", "Content deleted successfully!");
    } catch (error) {
      console.error(`Failed to delete content ${contentId}:`, error);
      GlobalService.showToast("error", "Delete Error", "Failed to delete content. Please try again.");
    } finally {
      setDeletingContent(null);
    }
  };

  const addNewSection = async () => {
    try {
      const newContentPayload = {
        type: { id: documentType },
        reviewCycle: { id: ContentReviewCycleEnum["30Days"] },
        autoSave: 5,
        settingLanguage: { id: englishLanguageId },
        isText: contentType === "text",
        contentTitle: "",
        contentDescription: ""
      };

      const createdContent = await createContent(newContentPayload);
      GlobalService.showToast("success", "New Section", "New section created successfully!");

      // Refresh content to get the latest data including the new section
      const contentResponse = await getContentByType(documentType);
      const freshContent = (contentResponse as any)?.payload?.data || [];
      
      if (Array.isArray(freshContent)) {
        const transformedContent = {};
        const translationsData = {};

        // Get existing translations to preserve them
        const existingTranslations = translationsByContent[documentType] || {};

        freshContent.forEach((item: any) => {
          transformedContent[item.id] = item;
          // Preserve existing translations or initialize empty for new content
          translationsData[item.id] = existingTranslations[item.id] || [];
        });
        
        // Update both content and translations state
        setContentByType(prev => ({
          ...prev,
          [documentType]: transformedContent
        }));

        setTranslationsByContent(prev => ({
          ...prev,
          [documentType]: translationsData
        }));

        // Update local content for immediate UI update
        setLocalContent(transformedContent);
      }
    } catch (error) {
      console.error("Failed to create new section:", error);
      GlobalService.showToast("error", "New Section Error", "Failed to create new section. Please try again.");
    }
  };

  const renderContentTab = () => {
    console.log("ContentTabs: renderContentTab - safeLocalContent:", safeLocalContent);
    console.log("ContentTabs: renderContentTab - Object.keys(safeLocalContent):", Object.keys(safeLocalContent));
    return (
      <Card className="p-4 sm:p-6" style={{ textAlign: "left" }}>
        <div className="space-y-4">
          {Object.keys(safeLocalContent).length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>No content sections available for this document type.</p>
            <p className="text-sm mt-2">Click &quot;Add New Section&quot; to get started.</p>
          </div>
        ) : (
          Object.entries(safeLocalContent).map(([key, section]: any) => (
            <div key={key} className="border rounded-lg overflow-hidden">
              <button 
                onClick={() => setExpandedSection(expandedSection === key ? null : key)} 
                className="w-full flex items-center justify-between p-3 sm:p-4 bg-gray-50 hover:bg-gray-100"
              >
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  <span className="font-medium text-sm sm:text-base">{section.contentTitle}</span>
                </div>
                <ChevronDown className={`h-4 w-4 transform transition-transform ${expandedSection === key ? "rotate-180" : ""}`} />
              </button>
              {expandedSection === key && (
                <div className="p-3 sm:p-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Section Title</label>
                    <input
                      type="text"
                      value={section.contentTitle}
                      onChange={(e) => handleContentChange(key, "contentTitle", e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <label className="block text-sm font-medium text-gray-700">Content</label>
                      <div className="flex rounded-md overflow-hidden border">
                        <button
                          className={`px-3 py-1 text-xs ${contentType === "text" ? "bg-blue-100 text-blue-700" : "bg-gray-100"}`}
                          onClick={() => setContentType("text")}
                        >
                          Text
                        </button>
                        <button
                          className={`px-3 py-1 text-xs ${contentType === "html" ? "bg-blue-100 text-blue-700" : "bg-gray-100"}`}
                          onClick={() => setContentType("html")}
                        >
                          HTML
                        </button>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      {contentType === "html" && <Code className="h-5 w-5 text-gray-400 mt-2" />}
                      <textarea
                        value={section.contentDescription}
                        onChange={(e) => handleContentChange(key, "contentDescription", e.target.value)}
                        rows={4}
                        className={`w-full px-3 py-2 border rounded-lg resize-none text-sm ${contentType === "html" ? "font-mono" : ""}`}
                      />
                    </div>
                    {contentType === "html" && (
                      <p className="text-xs text-gray-500 mt-1">HTML formatting is supported in this field.</p>
                    )}
                  </div>
                  <div className="flex flex-col sm:flex-row justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full sm:w-auto"
                      onClick={() => handleDelete(key)}
                      disabled={deletingContent === key}
                    >
                      <Trash2 className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                      {deletingContent === key ? "Deleting..." : "Delete"}
                    </Button>
                    <Button
                      size="sm"
                      className="w-full sm:w-auto"
                      onClick={() => handleSave(key)}
                      disabled={savingContent === key}
                    >
                      <Save className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                      {savingContent === key ? "Saving..." : "Save"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
        <Button variant="outline" onClick={addNewSection} className="w-full mt-4 text-sm sm:text-base">
          <Plus className="h-4 w-4 mr-2" />Add New Section
        </Button>
      </div>
    </Card>
    );
  };

  const renderSettingsTab = () => (
    <Card className="p-4 sm:p-6">
      <div className="space-y-6">
        <div>
          <h3 className="text-base sm:text-lg font-medium mb-4">Document Settings</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Default Language</label>
              <LanguageDropdown
                selectedLanguage={languages?.find((lang: any) => lang.id === selectedSettings?.language?.id) || null}
                onLanguageSelect={(language) => {
                  setSelectedSettings(prev => ({
                    ...prev,
                    language: { id: language?.id || "" }
                  }));
                }}
                placeholder="Select default language..."
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Review Cycle</label>
              <select
                className="w-full px-2 py-1 sm:px-3 sm:py-2 border rounded-lg text-sm"
                value={selectedSettings?.reviewCycle?.id || ""}
                onChange={(e) => setSelectedSettings(prev => ({
                  ...prev,
                  reviewCycle: { id: parseInt(e.target.value) }
                }))}
              >
                <option value="">Select Review Cycle</option>
                <option value={ContentReviewCycleEnum["30Days"]}>30 days</option>
                <option value={ContentReviewCycleEnum["60Days"]}>60 days</option>
                <option value={ContentReviewCycleEnum["90Days"]}>90 days</option>
              </select>
            </div>
          </div>
        </div>
        <div className="pt-6 border-t">
          <div className="flex justify-end">
            <Button
              onClick={handleSaveSettings}
              disabled={isSavingSettings || !selectedSettings?.language?.id || !selectedSettings?.reviewCycle?.id}
              className="px-6"
            >
              {isSavingSettings ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Settings
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );

  const renderTranslationsTab = () => (
    <Card className="p-4 sm:p-6">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between">
          <h3 className="text-base sm:text-lg font-medium mb-2 sm:mb-0">Available Translations</h3>
        </div>

        {Object.keys(safeLocalContent).length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Globe className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>No content sections available for translations.</p>
            <p className="text-sm mt-2">Create content sections first, then add translations.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(safeLocalContent).map(([contentId, contentItem]: any) => {
              const contentTranslations = translationsByContent[documentType]?.[contentId] || [];
              const hasTranslations = contentTranslations.length > 0;

              return (
                <div key={contentId} className="border rounded-lg overflow-hidden">
                  <div className="flex items-center justify-between p-4 bg-gray-50">
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-blue-600" />
                      <div>
                        <h4 className="font-medium text-sm sm:text-base">{contentItem.contentTitle || "Untitled Section"}</h4>
                        <p className="text-xs text-gray-500">Content ID: {contentId}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs bg-gray-200 px-2 py-1 rounded">
                            {contentTranslations.length} translation{contentTranslations.length !== 1 ? "s" : ""}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedContentForTranslation(contentItem);
                          setSelectedTranslation(null);
                          setShowTranslationModal(true);
                        }}
                      >
                        <Plus className="h-4 w-4 mr-1" />Add
                      </Button>
                    </div>
                  </div>

                  {hasTranslations && (
                    <div className="p-4 space-y-3">
                      {contentTranslations.map((translation: any, index: number) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <Globe className="h-4 w-4 text-gray-500" />
                            <div>
                              <div className="font-medium text-sm">
                                {languages?.find((lang: any) => lang.id === translation.language?.id)?.name || "Unknown Language"}
                              </div>
                              <div className="text-xs text-gray-500">
                                {translation.title || "No title"}
                              </div>
                              <div className="text-xs text-gray-400 mt-1">
                                Last updated: {translation.updatedAt ? new Date(translation.updatedAt).toLocaleDateString() : "Unknown"}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-xs"
                              onClick={() => {
                                setSelectedContentForTranslation(contentItem);
                                setSelectedTranslation(translation);
                                setShowTranslationModal(true);
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-xs text-red-600 hover:text-red-700"
                              onClick={() => handleDeleteTranslation(translation.id)}
                              disabled={deletingTranslation === translation.id}
                            >
                              {deletingTranslation === translation.id ? "Deleting..." : "Delete"}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Card>
  );

  const handleTranslationSave = async (formData: any) => {
    try {
      // Validate required data
      if (!selectedContentForTranslation || !selectedContentForTranslation.id) {
        throw new Error("No content selected for translation");
      }

      if (!formData.language || !formData.language.id) {
        throw new Error("Please select a language");
      }

      if (selectedTranslation) {
        // Update existing translation
        await dispatch(updateContentManagementTranslation({
          id: selectedTranslation.id,
          data: {
            content: formData.content,
            title: formData.title,
            language: { id: formData.language.id },
            contentManagementType: { id: selectedContentForTranslation.id }
          }
        })).unwrap();
        GlobalService.showToast("success", "Translation Updated", "Translation updated successfully!");
      } else {
        // Create new translation
        const payload = {
          content: formData.content,
          title: formData.title,
          language: { id: formData.language.id },
          contentManagementType: { id: selectedContentForTranslation.id }
        };
        
        await dispatch(createContentManagementTranslation(payload)).unwrap();
        GlobalService.showToast("success", "Translation Created", "Translation created successfully!");
      }
      
      // Refresh translations to show the updated data
      await refreshTranslations();
    } catch (error) {
      console.error("Failed to save translation:", error);
      throw error; // Re-throw to let the modal handle the error display
    }
  };

  const handleDeleteTranslation = async (translationId: string) => {
    if (!window.confirm("Are you sure you want to delete this translation? This action cannot be undone.")) {
      return;
    }

    try {
      setDeletingTranslation(translationId);
      
      // Call the delete translation action
      await dispatch(deleteContentManagementTranslation({ id: translationId })).unwrap();
      
      // Show success message
      GlobalService.showToast("success", "Translation Deleted", "Translation deleted successfully!");
      
      // Refresh translations to show the updated data
      await refreshTranslations();
    } catch (error) {
      console.error("Failed to delete translation:", error);
      GlobalService.showToast("error", "Delete Error", "Failed to delete translation. Please try again.");
    } finally {
      setDeletingTranslation(null);
    }
  };

  const renderTabContent = () => {
    switch (contentTab) {
      case "content":
        return renderContentTab();
      case "settings":
        return renderSettingsTab();
      case "translations":
        return renderTranslationsTab();
      default:
        return renderContentTab();
    }
  };

  return (
    <div className="space-y-6" style={{ textAlign: "left" }}>
      <div className="flex gap-2 relative z-20 bg-white p-2 rounded-lg border border-gray-200 shadow-sm">
        <Button 
          onClick={() => setContentTab("content")} 
          variant={contentTab === "content" ? "default" : "outline"}
          className={`px-4 py-2 rounded-md transition-all duration-200 ${
            contentTab === "content" 
              ? "bg-green-600 text-white shadow-md" 
              : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
          }`}
        >
          Content
        </Button>
        <Button 
          onClick={() => setContentTab("settings")} 
          variant={contentTab === "settings" ? "default" : "outline"}
          className={`px-4 py-2 rounded-md transition-all duration-200 ${
            contentTab === "settings" 
              ? "bg-green-600 text-white shadow-md" 
              : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
          }`}
        >
          Settings
        </Button>
        <Button 
          onClick={() => setContentTab("translations")} 
          variant={contentTab === "translations" ? "default" : "outline"}
          className={`px-4 py-2 rounded-md transition-all duration-200 ${
            contentTab === "translations" 
              ? "bg-green-600 text-white shadow-md" 
              : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
          }`}
        >
          Translations
        </Button>
      </div>
      {renderTabContent()}
      <TranslationModal
        isOpen={showTranslationModal}
        onClose={() => {
          setShowTranslationModal(false);
          setSelectedTranslation(null);
          setSelectedContentForTranslation(null);
        }}
        onSave={handleTranslationSave}
        selectedTranslation={selectedTranslation}
        selectedContentForTranslation={selectedContentForTranslation}
        languages={languages}
      />
    </div>
  );
};

export default ContentTabs;